import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Paper,
  useTheme,
  alpha,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Fade,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  Checkbox
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PreviewIcon from '@mui/icons-material/Preview';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SyncIcon from '@mui/icons-material/Sync';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import ConfirmationDialog from 'components/ConfirmationDialog';
import { regionSyncService } from '../services/RegionSyncService';
import { SyncConfig, SyncRequest, SyncPreview, SolutionInfo } from '../types';
import { logger } from 'utils/logger';

const log = logger.getLogger('SyncTab');

interface SyncTabProps {
  onSyncStarted?: () => void;
}

const SyncTab: React.FC<SyncTabProps> = ({ onSyncStarted }) => {
  const theme = useTheme();

  // State
  const [configs, setConfigs] = useState<SyncConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [syncType, setSyncType] = useState<'FULL' | 'SOLUTION'>('FULL');
  const [syncName, setSyncName] = useState<string>('');
  const [sourceConfigId, setSourceConfigId] = useState<number | ''>('');
  const [targetConfigId, setTargetConfigId] = useState<number | ''>('');
  const [sourceAccountId, setSourceAccountId] = useState<string>('');
  const [targetAccountId, setTargetAccountId] = useState<string>('');
  const [menuId, setMenuId] = useState<string>('');
  const [includeHistory, setIncludeHistory] = useState(false);
  const [createNewIds, setCreateNewIds] = useState(false);
  const [forceNewCopies, setForceNewCopies] = useState(false);

  // Solution copy state
  const [availableSolutions, setAvailableSolutions] = useState<SolutionInfo[]>([]);
  const [selectedSolutions, setSelectedSolutions] = useState<SolutionInfo[]>([]);
  const [loadingSolutions, setLoadingSolutions] = useState(false);

  // Preview state
  const [preview, setPreview] = useState<SyncPreview | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [executingSync, setExecutingSync] = useState(false);

  // Fetch configs on mount
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const data = await regionSyncService.getConfigs();
        setConfigs(data.filter(c => c.isActive));
      } catch (err) {
        log.error('Failed to fetch configs:', err);
      }
    };
    fetchConfigs();
  }, []);

  const baseCanSubmit =
    syncName.trim() &&
    sourceConfigId &&
    targetConfigId &&
    sourceConfigId !== targetConfigId &&
    sourceAccountId.trim() &&
    targetAccountId.trim() &&
    sourceAccountId !== targetAccountId;

  const canSubmit = syncType === 'FULL'
    ? baseCanSubmit && menuId.trim()
    : baseCanSubmit && selectedSolutions.length > 0;

  const buildRequest = (dryRun: boolean): SyncRequest => {
    if (syncType === 'SOLUTION') {
      return {
        syncName: syncName.trim(),
        sourceConfigId: sourceConfigId as number,
        targetConfigId: targetConfigId as number,
        source: { accountId: parseInt(sourceAccountId, 10) },
        target: { accountId: parseInt(targetAccountId, 10) },
        options: {
          solutionIds: selectedSolutions.map(s => s.id),
          includeHistory: false,
          createNewIds,
          forceNewCopies: createNewIds && forceNewCopies,
          dryRun
        }
      };
    }
    return {
      syncName: syncName.trim(),
      sourceConfigId: sourceConfigId as number,
      targetConfigId: targetConfigId as number,
      source: { accountId: parseInt(sourceAccountId, 10) },
      target: { accountId: parseInt(targetAccountId, 10) },
      options: {
        menuId: parseInt(menuId, 10),
        includeHistory,
        createNewIds,
        forceNewCopies: createNewIds && forceNewCopies,
        dryRun
      }
    };
  };

  // Fetch solutions when source config + account are set and sync type is SOLUTION
  const handleFetchSolutions = async () => {
    if (!sourceConfigId || !sourceAccountId.trim()) return;
    try {
      setLoadingSolutions(true);
      const solutions = await regionSyncService.getSolutions(sourceConfigId as number, parseInt(sourceAccountId, 10));
      setAvailableSolutions(solutions);
    } catch (err: any) {
      log.error('Failed to fetch solutions:', err);
      setError(err.message || 'Failed to fetch solutions');
    } finally {
      setLoadingSolutions(false);
    }
  };

  const handlePreview = async () => {
    if (!canSubmit) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const request = buildRequest(true);
      const previewResult = await regionSyncService.previewSync(request);
      setPreview(previewResult);
      setShowPreview(true);
    } catch (err: any) {
      log.error('Preview failed:', err);
      setError(err.message || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteClick = () => {
    setConfirmDialogOpen(true);
  };

  const handleExecuteSync = async () => {
    try {
      setExecutingSync(true);
      setConfirmDialogOpen(false);
      setError(null);

      const request = buildRequest(false);
      const result = syncType === 'SOLUTION'
        ? await regionSyncService.executeSolutionCopy(request)
        : await regionSyncService.executeSync(request);
      setSuccess(`Sync started! Sync ID: ${result.syncId || 'N/A'}. Switching to History tab to track progress...`);

      // Clear preview
      setPreview(null);
      setShowPreview(false);

      // Switch to History tab after a brief delay so user sees the success message
      if (onSyncStarted) {
        setTimeout(() => onSyncStarted(), 1500);
      }
    } catch (err: any) {
      log.error('Sync execution failed:', err);
      setError(err.message || 'Sync failed to start');
    } finally {
      setExecutingSync(false);
    }
  };

  const getConfigName = (id: number | '') => {
    if (!id) return '';
    const config = configs.find(c => c.id === id);
    return config ? `${config.name} (${config.environment})` : '';
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Fade in={true} timeout={600}>
        <Paper
          elevation={2}
          sx={{
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)'
            }
          }}
        >
          {/* Header */}
          <Box
            p={2.5}
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              background: theme.palette.background.default
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {syncType === 'FULL' ? 'Full Sync (Menu + Solutions)' : 'Solution Copy'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {syncType === 'FULL'
                    ? 'Copy a complete menu with all its solutions from one database to another.'
                    : 'Copy specific solutions (without menu/items) from one account to another with new IDs.'}
                </Typography>
              </Box>
              <ToggleButtonGroup
                value={syncType}
                exclusive
                onChange={(_, value) => { if (value) setSyncType(value); }}
                size="small"
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 2 } }}
              >
                <ToggleButton value="FULL">
                  <SyncIcon sx={{ mr: 0.5, fontSize: 18 }} /> Full Sync
                </ToggleButton>
                <ToggleButton value="SOLUTION">
                  <ContentCopyIcon sx={{ mr: 0.5, fontSize: 18 }} /> Solution Copy
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Form Content */}
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Sync Name */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Sync Name"
                  value={syncName}
                  onChange={(e) => setSyncName(e.target.value)}
                  fullWidth
                  required
                  placeholder="e.g., Prod → Staging: Menu 1124 - Feb 2026"
                  helperText="Give this sync a descriptive name for easy identification in history"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.light
                      }
                    }
                  }}
                />
              </Grid>

              {/* Source Configuration */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Box
                  sx={{
                    p: 2.5,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.06)
                    }
                  }}
                >
                  <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                    Source
                  </Typography>

                  <TextField
                    select
                    label="Source Database"
                    value={sourceConfigId}
                    onChange={(e) => setSourceConfigId(e.target.value === '' ? '' : Number(e.target.value))}
                    fullWidth
                    required
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="">Select source...</MenuItem>
                    {configs.map(config => (
                      <MenuItem
                        key={config.id}
                        value={config.id}
                        disabled={config.id === targetConfigId}
                      >
                        {config.name} ({config.environment})
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label="Source Account ID"
                    type="number"
                    value={sourceAccountId}
                    onChange={(e) => setSourceAccountId(e.target.value)}
                    fullWidth
                    required
                    placeholder="e.g., 123"
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />

                  {syncType === 'FULL' && (
                    <TextField
                      label="Menu ID"
                      type="number"
                      value={menuId}
                      onChange={(e) => setMenuId(e.target.value)}
                      fullWidth
                      required
                      placeholder="e.g., 1124"
                      helperText="Sync one menu at a time"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                </Box>
              </Grid>

              {/* Arrow */}
              <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ArrowForwardIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              </Grid>

              {/* Target Configuration */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Box
                  sx={{
                    p: 2.5,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.04),
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.secondary.main, 0.06)
                    }
                  }}
                >
                  <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ fontWeight: 600 }}>
                    Target
                  </Typography>

                  <TextField
                    select
                    label="Target Database"
                    value={targetConfigId}
                    onChange={(e) => setTargetConfigId(e.target.value === '' ? '' : Number(e.target.value))}
                    fullWidth
                    required
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="">Select target...</MenuItem>
                    {configs.map(config => (
                      <MenuItem
                        key={config.id}
                        value={config.id}
                        disabled={config.id === sourceConfigId}
                      >
                        {config.name} ({config.environment})
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label="Target Account ID"
                    type="number"
                    value={targetAccountId}
                    onChange={(e) => setTargetAccountId(e.target.value)}
                    fullWidth
                    required
                    placeholder="e.g., 456"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Box>
              </Grid>

              {/* Solution Picker (SOLUTION mode only) */}
              {syncType === 'SOLUTION' && (
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backgroundColor: alpha(theme.palette.primary.main, 0.02)
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Select Solutions to Copy
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleFetchSolutions}
                        disabled={!sourceConfigId || !sourceAccountId.trim() || loadingSolutions}
                        startIcon={loadingSolutions ? <CircularProgress size={16} /> : undefined}
                        sx={{ textTransform: 'none', borderRadius: '8px' }}
                      >
                        {loadingSolutions ? 'Loading...' : 'Fetch Solutions'}
                      </Button>
                    </Box>

                    {availableSolutions.length > 0 ? (
                      <Autocomplete
                        multiple
                        options={availableSolutions}
                        value={selectedSolutions}
                        onChange={(_, newValue) => setSelectedSolutions(newValue)}
                        getOptionLabel={(option) => `${option.name} (ID: ${option.id}, ${option.solution_type})`}
                        disableCloseOnSelect
                        renderOption={(props, option, { selected }) => {
                          const { key, ...restProps } = props as any;
                          return (
                            <li key={key} {...restProps}>
                              <Checkbox
                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                checked={selected}
                                sx={{ mr: 1 }}
                              />
                              <Box>
                                <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {option.id} | {option.solution_type} | {option.efg_type || 'NONE'}
                                </Typography>
                              </Box>
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Solutions"
                            placeholder={selectedSolutions.length === 0 ? 'Search and select solutions...' : ''}
                            helperText={`${selectedSolutions.length} of ${availableSolutions.length} solutions selected`}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                              <Chip
                                key={key}
                                label={`${option.name} (${option.id})`}
                                size="small"
                                {...tagProps}
                                sx={{ borderRadius: '8px' }}
                              />
                            );
                          })
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: 2 },
                          mb: 1
                        }}
                      />
                    ) : (
                      <Alert severity="info" variant="outlined">
                        Select a source database and account, then click "Fetch Solutions" to load available solutions.
                      </Alert>
                    )}

                    {selectedSolutions.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`${selectedSolutions.length} solutions selected`}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 500, borderRadius: '8px' }}
                        />
                        <Button
                          size="small"
                          onClick={() => setSelectedSolutions(availableSolutions)}
                          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        >
                          Select All
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setSelectedSolutions([])}
                          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        >
                          Clear All
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Options */}
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.background.default, 0.5)
                    }
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Options
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {syncType === 'FULL' && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={includeHistory}
                            onChange={(e) => setIncludeHistory(e.target.checked)}
                          />
                        }
                        label="Include history tables"
                      />
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={createNewIds}
                          onChange={(e) => {
                            setCreateNewIds(e.target.checked);
                            if (!e.target.checked) setForceNewCopies(false);
                          }}
                          color="success"
                        />
                      }
                      label="Create new IDs"
                    />
                    {createNewIds && syncType === 'FULL' && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={forceNewCopies}
                            onChange={(e) => setForceNewCopies(e.target.checked)}
                            color="warning"
                          />
                        }
                        label="Force fresh copies"
                      />
                    )}
                  </Box>
                  {createNewIds && syncType === 'FULL' && !forceNewCopies && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      Solutions will get new auto-generated IDs on first run. Re-running will update previously synced solutions.
                    </Alert>
                  )}
                  {createNewIds && syncType === 'FULL' && forceNewCopies && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      Every run will create brand new copies of solutions and all their child tables (components, pages, questions, valedictions, etc.), even if they already exist in the target.
                    </Alert>
                  )}
                  {createNewIds && syncType === 'SOLUTION' && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      Every run will create fresh copies of selected solutions and all their child tables. Items are not copied (they are menu-scoped). Account-level questions are automatically included.
                    </Alert>
                  )}
                  {!createNewIds && syncType === 'SOLUTION' && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      Without "Create new IDs", solutions will be copied with their original IDs. If a solution with the same ID already exists in the target, it will be updated instead of creating a new one.
                    </Alert>
                  )}
                </Box>
              </Grid>

              {/* Preview */}
              {showPreview && preview && (
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      backgroundColor: alpha(theme.palette.info.main, 0.04)
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                      Preview Results
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={`${preview.totalRecords || 0} total records`}
                        sx={{ mr: 1, fontWeight: 500, borderRadius: '8px' }}
                      />
                      {preview.estimatedDuration && (
                        <Chip
                          label={`Est. ${preview.estimatedDuration}`}
                          variant="outlined"
                          sx={{ fontWeight: 500, borderRadius: '8px' }}
                        />
                      )}
                    </Box>
                    {preview.tables && preview.tables.length > 0 ? (
                      <List dense>
                        {preview.tables.map((table, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              <TableChartIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight={500}>
                                  {table.tableName}
                                </Typography>
                              }
                              secondary={`${table.recordCount} records - ${table.action}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No table details available. Check sync stats in the response.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Actions Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              backgroundColor: theme.palette.background.default
            }}
          >
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={20} /> : <PreviewIcon />}
              onClick={handlePreview}
              disabled={!canSubmit || loading || executingSync}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '12px',
                px: 2.5,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              startIcon={executingSync ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              onClick={handleExecuteClick}
              disabled={!canSubmit || loading || executingSync}
              color="warning"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '12px',
                px: 2.5,
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 14px rgba(0,0,0,0.14)'
                }
              }}
            >
              Execute Sync
            </Button>
          </Box>
        </Paper>
      </Fade>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        title="Confirm Sync Execution"
        content={
          <Box>
            <Typography gutterBottom>
              You are about to {syncType === 'SOLUTION' ? 'copy solutions' : 'sync data'}:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Type:</strong> {syncType === 'SOLUTION' ? 'Solution Copy' : 'Full Sync'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Source:</strong> {getConfigName(sourceConfigId)} — Account {sourceAccountId}
              {syncType === 'FULL' && `, Menu ${menuId}`}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Target:</strong> {getConfigName(targetConfigId)} — Account {targetAccountId}
            </Typography>
            {syncType === 'SOLUTION' && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Solutions:</strong> {selectedSolutions.length} selected ({selectedSolutions.map(s => s.id).join(', ')})
              </Typography>
            )}
            {(createNewIds || syncType === 'SOLUTION') && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Mode:</strong> Create New IDs (solutions will get new auto-generated IDs)
              </Typography>
            )}
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will modify data in the target database. Make sure you have reviewed the preview first.
            </Alert>
          </Box>
        }
        confirmButtonText="Execute Sync"
        severity="warning"
        onConfirm={handleExecuteSync}
        onCancel={() => setConfirmDialogOpen(false)}
      />
    </Box>
  );
};

export default SyncTab;
